if (!customElements.get('cts-customer-reels')) {
  class CTSCustomerReels extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.triggers = [...this.querySelectorAll('[data-reel-trigger]')];
      this.clips = [...this.querySelectorAll('[data-reel-template]')];
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.muted = false;
      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.open(index, trigger), { signal: this.events.signal });
      });
      this.setupPreview();
    }

    open(index, trigger, source = 'thumbnail') {
      document.querySelectorAll('cts-customer-reels').forEach((reels) => {
        if (reels !== this) reels.close?.();
      });
      this.stopPreviewTrigger?.();
      const previewSeconds = source === 'preview' ? this.previewSeconds() : undefined;
      this.removePreview();
      if (!this.viewer) this.createViewer();
      const wasOpen = this.viewer.open;
      const previousIndex = this.index;
      const previousVideoId = this.stage.firstElementChild?.dataset.videoId;
      window.pauseAllMedia?.();
      this.opener = trigger;
      document.body.classList.add('cts-reels-open');
      if (!this.viewer.open) {
        if (this.mobile.matches) {
          this.previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.locked = true;
          this.viewer.showModal();
        } else {
          this.viewer.show();
        }
      }
      if (!wasOpen && !this.mobile.matches) this.watchStickyCart();
      if (!wasOpen) this.openSource = source;
      if (source === 'preview') this.ignoreViewerClicksUntil = Date.now() + 400;
      this.showClip(index, index > previousIndex ? 'next' : 'previous');
      if (!wasOpen) this.trackInteraction('opened', { open_source: source, preview_seconds: previewSeconds });
      else if (index !== previousIndex) this.trackInteraction('changed', { previous_video_id: previousVideoId });
      this.viewer.querySelector('[data-reel-close]').focus({ preventScroll: true });
    }

    createViewer() {
      this.viewer = this.querySelector('[data-reel-viewer]').content.firstElementChild.cloneNode(true);
      document.body.append(this.viewer);
      const on = (element, event, handler) => element.addEventListener(event, handler, { signal: this.events.signal });
      this.stage = this.viewer.querySelector('.cts-reels__stage');
      this.spinner = this.viewer.querySelector('.cts-reels__spinner');
      this.progress = this.viewer.querySelector('.cts-reels__progress');
      this.playButton = this.viewer.querySelector('[data-reel-play]');
      this.soundButton = this.viewer.querySelector('[data-reel-sound]');
      this.message = this.viewer.querySelector('.cts-reels__message');
      this.resizeHandle = document.createElement('button');
      this.resizeHandle.type = 'button';
      this.resizeHandle.className = 'cts-reels__resize';
      this.resizeHandle.setAttribute('aria-label', 'Resize Reel window');
      this.resizeHandle.title = 'Drag to resize. Arrow keys adjust size. Double-click or press Home to reset.';
      this.resizeHandle.innerHTML = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M0 8L8 0M0 14L14 0M0 20L20 0"/></svg>';
      this.viewer.append(this.resizeHandle);
      on(this.resizeHandle, 'pointerdown', (event) => {
        if (this.mobile.matches || event.button !== 0 || this.resizeDrag) return;
        event.preventDefault();
        this.resizeHandle.focus({ preventScroll: true });
        this.resizeDrag = { id: event.pointerId, x: event.clientX, y: event.clientY, width: this.viewer.getBoundingClientRect().width };
        this.resizeHandle.setPointerCapture(event.pointerId);
        this.viewer.classList.add('cts-reels-viewer--resizing');
      });
      on(this.resizeHandle, 'pointermove', (event) => {
        if (event.pointerId !== this.resizeDrag?.id) return;
        const { x, y, width } = this.resizeDrag;
        const ratio = 16 / 9;
        this.resizeViewer(width + ((x - event.clientX) + ratio * (y - event.clientY)) / (1 + ratio * ratio));
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((event) => on(this.resizeHandle, event, () => this.endResize()));
      on(this.resizeHandle, 'dblclick', () => this.viewer.style.removeProperty('--cts-reels-width'));
      on(this.resizeHandle, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'Home') this.viewer.style.removeProperty('--cts-reels-width');
        else this.resizeViewer(this.viewer.getBoundingClientRect().width + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? 16 : -16));
      });
      // The viewer opens under the preview's corner, so a double-click's second click would otherwise pause, mute or seek it.
      const ignoreViewerClick = (event) => {
        if (Date.now() >= (this.ignoreViewerClicksUntil || 0) || (event.type === 'click' && !event.detail)) return;
        event.preventDefault();
        event.stopPropagation();
      };
      ['pointerdown', 'click'].forEach((type) => this.viewer.addEventListener(type, ignoreViewerClick, { capture: true, signal: this.events.signal }));
      on(window, 'resize', () => { this.endResize(); this.updateCartClearance(); });
      on(window, 'blur', () => { this.endResize(); this.scrubbing = false; });
      on(this.viewer.querySelector('[data-reel-close]'), 'click', () => this.close());
      on(this.viewer.querySelector('[data-reel-prev]'), 'click', () => this.navigate(-1));
      on(this.viewer.querySelector('[data-reel-next]'), 'click', () => this.navigate(1));
      on(this.playButton, 'click', () => this.togglePlayback());
      on(this.soundButton, 'click', () => {
        this.muted = !this.video.muted;
        this.video.muted = this.muted;
        this.updateSound();
      });
      on(this.progress, 'pointerdown', () => { this.scrubbing = true; });
      ['pointerup', 'pointercancel'].forEach((event) => on(window, event, () => { this.scrubbing = false; }));
      on(this.progress, 'change', () => { this.scrubbing = false; });
      on(this.progress, 'input', () => {
        if (Number.isFinite(this.video.duration)) this.video.currentTime = this.video.duration * this.progress.value / 100;
      });
      on(this.viewer, 'cancel', (event) => { event.preventDefault(); this.close(); });
      on(this.viewer, 'keydown', (event) => {
        if (event.target === this.progress) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.navigate(event.key === 'ArrowDown' ? 1 : -1);
        }
      });
      on(document, 'keydown', (event) => {
        if (event.key !== 'Escape' || event.defaultPrevented || !this.viewer.open) return;
        // Let a modal above the non-modal desktop player handle its own Escape.
        const otherModal = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some((element) =>
          element !== this.viewer && !element.closest('details:not([open])') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        if (otherModal) return;
        event.preventDefault();
        this.close();
      });
      on(this.stage, 'click', () => this.togglePlayback());
      on(this.stage, 'touchstart', (event) => {
        this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      });
      on(this.stage, 'touchend', (event) => {
        if (!this.touch || !this.mobile.matches) return;
        const deltaY = event.changedTouches[0].clientY - this.touch.y;
        const deltaX = event.changedTouches[0].clientX - this.touch.x;
        this.touch = null;
        if (Math.abs(deltaY) > 60 && Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
          this.ignoreClickUntil = Date.now() + 400;
          this.navigate(deltaY < 0 ? 1 : -1);
        }
      });
      on(this.stage, 'touchcancel', () => { this.touch = null; });
      on(document, 'visibilitychange', () => {
        if (!document.hidden) return;
        this.video?.pause();
        this.endWatch('page_hidden');
      });
      on(window, 'pagehide', () => this.endWatch('page_hidden'));
      on(this.mobile, 'change', () => this.updateViewerMode());
    }

    updateViewerMode() {
      if (!this.viewer.open) return;
      const wasPlaying = this.video && !this.video.paused;
      const focused = this.viewer.contains(document.activeElement) ? document.activeElement : null;
      this.endResize();
      this.stopWatchingStickyCart();
      this.viewer.close();
      if (this.mobile.matches) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.locked = true;
        this.viewer.showModal();
      } else {
        if (this.locked) document.body.style.overflow = this.previousOverflow;
        this.locked = false;
        this.viewer.show();
        this.watchStickyCart();
      }
      focused?.focus({ preventScroll: true });
      if (wasPlaying && this.video.paused) this.play();
    }

    showClip(index, exitReason) {
      if (index < 0 || index >= this.clips.length) return;
      // Re-tapping the Reel already on screen restarts it within the same watch session.
      if (this.watch?.index !== index) this.endWatch(exitReason);
      this.video?.pause();
      this.clipEvents?.abort();
      this.clipEvents = new AbortController();
      this.index = index;
      const clip = this.clips[index].content.cloneNode(true);
      const nextVideo = clip.querySelector('video');
      if (this.video) {
        // Keep the media element's playback permission across successive Reels.
        this.video.replaceChildren(...nextVideo.childNodes);
        this.video.poster = nextVideo.poster;
        nextVideo.replaceWith(this.video);
      } else {
        this.video = nextVideo;
      }
      this.stage.replaceChildren(clip);
      this.scrubbing = false;
      this.video.muted = this.muted;
      this.video.loop = this.clips.length === 1;
      this.progress.value = 0;
      this.progress.setAttribute('aria-valuetext', '0 seconds');
      this.touch = null;
      this.message.hidden = true;
      this.hideSpinner();
      this.viewer.setAttribute('aria-label', `Customer Reel by ${this.stage.querySelector('[data-reel-customer]').textContent}`);
      this.viewer.querySelector('[data-reel-prev]').disabled = index === 0;
      this.viewer.querySelector('[data-reel-next]').disabled = index === this.clips.length - 1;
      this.viewer.querySelector('[data-reel-count]').textContent = `${index + 1} / ${this.clips.length}`;
      this.updateSound();
      this.updatePlayback();
      const on = (event, handler) => this.video.addEventListener(event, handler, { signal: this.clipEvents.signal });
      on('play', () => {
        this.updatePlayback();
        // Playback resuming after a page_hidden flush is a new session; a paused Reel closed later is not.
        if (!this.watch) this.startWatch();
      });
      on('pause', () => { this.updatePlayback(); this.hideSpinner(); });
      on('seeking', () => { if (this.watch) this.rebaseWatch(this.watch, this.video); });
      on('timeupdate', () => {
        if (this.watch) this.advanceWatch(this.watch, this.video);
        if (!this.scrubbing && Number.isFinite(this.video.duration) && this.video.duration > 0) {
          this.progress.value = this.video.currentTime / this.video.duration * 100;
          this.progress.setAttribute('aria-valuetext', `${Math.floor(this.video.currentTime)} of ${Math.ceil(this.video.duration)} seconds`);
        }
      });
      on('ended', () => {
        this.hideSpinner();
        if (this.index < this.clips.length - 1) this.showClip(this.index + 1, 'ended');
      });
      const handleMediaError = () => {
        this.video.pause();
        this.showPlaybackError();
        this.updatePlayback();
      };
      on('error', handleMediaError);
      // A failed <source> does not bubble an error to <video> in every browser.
      const sources = [...this.video.querySelectorAll('source')];
      let failedSources = 0;
      sources.forEach((source) => source.addEventListener('error', () => {
        failedSources += 1;
        if (failedSources === sources.length) handleMediaError();
      }, { once: true, signal: this.clipEvents.signal }));
      this.video.load();
      if (!this.watch) this.startWatch();
      this.play();
      this.startWatchdog();
    }

    startWatch() {
      this.watch = { index: this.index, clip: this.stage.firstElementChild, source: this.openSource, seconds: 0 };
      this.rebaseWatch(this.watch, this.video);
    }

    rebaseWatch(watch, video) {
      watch.time = video.currentTime;
      watch.at = performance.now();
    }

    // Counts only progress real playback could have made since the last sample, so seeks, loops and restarts add nothing.
    advanceWatch(watch, video) {
      const delta = video.currentTime - watch.time;
      if (delta > 0 && delta <= (performance.now() - watch.at) / 1000 * 1.5 + 0.25) watch.seconds += delta;
      this.rebaseWatch(watch, video);
    }

    endWatch(exitReason) {
      const watch = this.watch;
      if (!watch) return;
      this.watch = null;
      this.advanceWatch(watch, this.video);
      this.trackInteraction('watched', {
        videoElement: watch.clip,
        video_index: watch.index + 1,
        open_source: watch.source,
        exit_reason: exitReason,
        watch_seconds: watch.seconds,
        video_duration: this.video.duration,
      });
    }

    watchStickyCart() {
      const watch = () => {
        const cart = document.querySelector('.zrx-sticky-atc-bar');
        if (cart === this.stickyCart) return;
        this.cartSizeObserver?.disconnect();
        this.cartStateObserver?.disconnect();
        this.stickyCart = cart;
        if (cart) {
          this.cartSizeObserver = new ResizeObserver(() => this.updateCartClearance());
          this.cartSizeObserver.observe(cart);
          this.cartStateObserver = new MutationObserver(() => this.updateCartClearance());
          this.cartStateObserver.observe(cart, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        }
        this.updateCartClearance();
      };
      watch();
      // The app can insert or replace its bar after the viewer has opened.
      this.cartMountObserver = new MutationObserver((records) => {
        if (this.cartWatchFrame) return;
        const changed = records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
          node.nodeType === 1 && (node.matches('.zrx-sticky-atc-bar') || node.querySelector('.zrx-sticky-atc-bar'))));
        if (!changed) return;
        this.cartWatchFrame = requestAnimationFrame(() => { this.cartWatchFrame = null; watch(); });
      });
      this.cartMountObserver.observe(document.body, { childList: true, subtree: true });
      this.updateCartClearance();
    }

    stopWatchingStickyCart() {
      this.cartSizeObserver?.disconnect();
      this.cartStateObserver?.disconnect();
      this.cartMountObserver?.disconnect();
      cancelAnimationFrame(this.cartWatchFrame);
      this.cartWatchFrame = null;
      this.stickyCart = null;
    }

    updateCartClearance() {
      if (!this.viewer?.open || this.mobile.matches) return;
      const cart = this.stickyCart;
      let bottom = 24;
      if (cart?.classList.contains('zrx-sticky-atc-show') && cart.classList.contains('zrx-sticky-atc-bar-bottom')) {
        const style = getComputedStyle(cart);
        if (style.display !== 'none' && style.visibility !== 'hidden') {
          bottom = Math.max(24, cart.getBoundingClientRect().height + Math.max(0, parseFloat(style.bottom) || 0) + 12);
        }
      }
      this.viewerBottom = bottom;
      this.viewer.style.setProperty('--cts-reels-bottom', `${bottom}px`);
    }

    setupPreview() {
      if (!this.clips.length || document.querySelector('cts-customer-reels') !== this) return;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Start once the Reels row has scrolled above the viewport, ie the shopper passed it without opening one.
      // A scroll check rather than IntersectionObserver: a fast jump past the row never fires an intersection change.
      const scrollEvents = new AbortController();
      let frame = null;
      const check = () => {
        frame = null;
        if (!this.getClientRects().length || this.getBoundingClientRect().bottom > 0) return;
        this.stopPreviewTrigger();
        this.showPreview();
      };
      this.stopPreviewTrigger = () => { scrollEvents.abort(); cancelAnimationFrame(frame); };
      window.addEventListener('scroll', () => { frame ??= requestAnimationFrame(check); }, { passive: true, signal: scrollEvents.signal });
      check();
    }

    showPreview() {
      if (this.preview || this.viewer?.open) return;
      this.preview = this.querySelector('[data-reel-preview]').content.firstElementChild.cloneNode(true);
      this.previewEvents = new AbortController();
      const on = (element, event, handler, options) => element.addEventListener(event, handler, { ...options, signal: this.previewEvents.signal });
      this.previewOpen = this.preview.querySelector('[data-reel-preview-open]');
      this.previewBar = this.preview.querySelector('.cts-reels-preview__progress span');
      // The full viewer starts the Reel from the beginning, with sound.
      const expand = () => this.open(this.previewIndex, this.triggers[this.previewIndex], 'preview');
      on(this.previewOpen, 'click', expand);
      on(this.preview.querySelector('[data-reel-preview-tab]'), 'click', expand);
      on(this.preview.querySelector('[data-reel-preview-close]'), 'click', () => {
        const seconds = this.previewSeconds();
        // Removing the focused button would drop keyboard focus to <body>. Chrome also focuses a clicked button,
        // so only keyboard focus moves on: after a mouse click, Space should still scroll the page.
        const keyboardFocus = this.preview.querySelector(':focus-visible');
        this.removePreview();
        this.trackPreview('preview_closed', { preview_seconds: seconds });
        if (keyboardFocus) this.triggers[0]?.focus({ preventScroll: true });
      });
      on(document, 'visibilitychange', () => this.updatePreviewPlayback());
      // Dawn's pauseAllMedia() also pauses the preview, so resume once the video that took over stops.
      const resume = (event) => {
        if (event.target.tagName === 'VIDEO' && !event.target.closest('[data-reels-preview]')) this.playPreview();
      };
      on(document, 'pause', resume, { capture: true });
      on(document, 'ended', resume, { capture: true });
      document.body.append(this.preview);
      // Lets the Google badge in theme.liquid give up the shared corner.
      document.documentElement.setAttribute('data-cts-reel-preview', '');
      // The CSS hides the card while Dawn's drawers and search modal mark <body>; pause with it.
      let covered = this.previewCovered();
      this.previewCoverObserver = new MutationObserver(() => {
        if (!this.preview || covered === this.previewCovered()) return;
        covered = !covered;
        this.updatePreviewPlayback();
      });
      this.previewCoverObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.previewWatch ??= { seconds: 0 };
      // Report exposure once per page, after the autoplay outcome is known.
      let timer;
      this.reportPreviewShown = (autoplay) => {
        clearTimeout(timer);
        if (this.previewShownTracked) return;
        this.previewShownTracked = true;
        this.trackPreview('preview_shown', { autoplay }, 0);
      };
      // play() can stay pending on a stalled network; the exposure still counts.
      timer = setTimeout(() => this.reportPreviewShown?.('pending'), 3000);
      this.showPreviewClip(0, this.reportPreviewShown);
      requestAnimationFrame(() => this.preview?.classList.add('cts-reels-preview--visible'));
    }

    previewCovered() {
      return getComputedStyle(this.preview).visibility === 'hidden';
    }

    previewSeconds() {
      if (this.previewVideo) this.advanceWatch(this.previewWatch, this.previewVideo);
      return this.previewWatch?.seconds || 0;
    }

    showPreviewClip(index, report) {
      if (this.previewVideo) this.advanceWatch(this.previewWatch, this.previewVideo);
      this.unloadPreviewVideo();
      this.previewIndex = index;
      const clip = this.clips[index].content;
      const video = clip.querySelector('video').cloneNode(true);
      const customer = clip.querySelector('[data-reel-customer]').textContent;
      video.removeAttribute('aria-label');
      video.setAttribute('aria-hidden', 'true');
      video.muted = true;
      video.defaultMuted = true;
      video.preload = 'auto';
      this.previewOpen.setAttribute('aria-label', `Watch ${customer}'s Reel with sound`);
      this.previewVideo = video;
      this.rebaseWatch(this.previewWatch, video);
      this.previewOpen.prepend(video);
      this.previewBar.style.transform = 'scaleX(0)';
      this.previewClipEvents = new AbortController();
      const on = (event, handler) => video.addEventListener(event, handler, { signal: this.previewClipEvents.signal });
      const sync = () => this.preview?.toggleAttribute('data-paused', video.paused);
      on('play', sync);
      on('pause', sync);
      on('timeupdate', () => {
        this.advanceWatch(this.previewWatch, video);
        if (video.duration > 0) this.previewBar.style.transform = `scaleX(${video.currentTime / video.duration})`;
      });
      // One pass through the Reels, then rest on the last one until the shopper taps it.
      on('ended', () => {
        if (this.previewIndex < this.clips.length - 1) this.showPreviewClip(this.previewIndex + 1);
        else this.previewFinished = true;
      });
      sync();
      this.playPreview(report);
    }

    // A detached <video> keeps its buffered media until its source is cleared.
    unloadPreviewVideo() {
      const video = this.previewVideo;
      if (!video) return;
      this.previewClipEvents.abort();
      this.previewVideo = null;
      video.pause();
      video.querySelectorAll('source').forEach((source) => source.remove());
      video.removeAttribute('src');
      video.load();
      video.remove();
    }

    updatePreviewPlayback() {
      const video = this.previewVideo;
      if (!video) return;
      if (!document.hidden && !this.previewCovered()) return this.playPreview();
      // Settle a pending autoplay report first: pause() rejects a pending play().
      this.reportPreviewShown?.('hidden');
      this.advanceWatch(this.previewWatch, video);
      video.pause();
    }

    playPreview(report) {
      const video = this.previewVideo;
      if (!video || this.previewFinished) return;
      if (!video.paused) return report?.('playing');
      if (document.hidden || this.previewCovered()) return report?.('hidden');
      // Stay still for reduced motion or while another page video is playing. A loaded YouTube embed may be paused, so only native playback counts.
      if (this.reducedMotion.matches) return report?.('reduced_motion');
      const otherMediaPlaying = [...document.querySelectorAll('video')].some((other) => other !== video && !other.paused && !other.ended);
      if (otherMediaPlaying) return report?.('other_media');
      video.play()?.then(() => report?.('playing'), () => {
        if (video === this.previewVideo) this.preview.toggleAttribute('data-paused', video.paused);
        report?.('blocked');
      });
    }

    removePreview() {
      if (!this.preview) return;
      // A tap or close before play() settles must not report its abort as blocked autoplay.
      this.reportPreviewShown?.('pending');
      this.reportPreviewShown = null;
      this.previewEvents.abort();
      this.previewCoverObserver.disconnect();
      this.unloadPreviewVideo();
      this.preview.remove();
      this.preview = null;
      document.documentElement.removeAttribute('data-cts-reel-preview');
    }

    resizeViewer(width) {
      if (this.mobile.matches) return;
      const maxWidth = Math.min(window.innerWidth - 48, (window.innerHeight - (this.viewerBottom || 24) - 24) * 9 / 16);
      const minWidth = Math.min(240, maxWidth);
      this.viewer.style.setProperty('--cts-reels-width', `${Math.max(minWidth, Math.min(maxWidth, width))}px`);
    }

    endResize() {
      const pointerId = this.resizeDrag?.id;
      this.resizeDrag = null;
      this.viewer?.classList.remove('cts-reels-viewer--resizing');
      if (pointerId !== undefined && this.resizeHandle.hasPointerCapture(pointerId)) this.resizeHandle.releasePointerCapture(pointerId);
    }

    navigate(direction) {
      if (!this.viewer?.open) return;
      const nextIndex = this.index + direction;
      if (nextIndex < 0 || nextIndex >= this.clips.length || nextIndex === this.index) return;
      const previousVideoId = this.stage.firstElementChild?.dataset.videoId;
      this.showClip(nextIndex, direction > 0 ? 'next' : 'previous');
      this.trackInteraction('changed', { previous_video_id: previousVideoId });
    }

    trackInteraction(action, detail) {
      // A detached element's events never reach the document listener in cts-product-analytics.js.
      (this.isConnected ? this : document).dispatchEvent(new CustomEvent('cts:reels-interaction', {
        bubbles: true,
        detail: {
          action,
          videoElement: this.stage?.firstElementChild,
          video_index: this.index + 1,
          reel_count: this.clips.length,
          ...detail,
        },
      }));
    }

    trackPreview(action, detail, index = this.previewIndex) {
      this.trackInteraction(action, { videoElement: this.clips[index].content.firstElementChild, video_index: index + 1, ...detail });
    }

    play() {
      window.pauseAllMedia?.();
      const video = this.video;
      const signal = this.clipEvents.signal;
      const attempt = video.play();
      attempt?.catch((error) => {
        if (signal.aborted || this.video !== video || !this.viewer?.open || error.name === 'AbortError') return;
        if (error.name === 'NotAllowedError') {
          this.hideSpinner();
          this.message.textContent = 'Tap play to watch this Reel.';
          this.message.hidden = false;
        } else {
          this.showPlaybackError();
        }
        this.updatePlayback();
      });
    }

    startWatchdog() {
      this.stopWatchdog();
      this.lastTime = this.video.currentTime;
      this.lastProgressAt = performance.now();
      this.watchdog = setInterval(() => this.checkProgress(), 100);
    }

    stopWatchdog() {
      clearInterval(this.watchdog);
      this.watchdog = null;
    }

    // Safari's native HLS player fires `seeked` before the data arrives and skips `waiting`, so watch real progress instead of events.
    checkProgress() {
      const video = this.video;
      if (!video || !this.viewer?.open) return;
      const idle = video.paused || video.ended || !this.message.hidden;
      if (idle || video.currentTime !== this.lastTime) {
        this.lastTime = video.currentTime;
        this.lastProgressAt = performance.now();
        this.hideSpinner();
        return;
      }
      if (this.spinner && performance.now() - this.lastProgressAt >= 500) this.spinner.hidden = false;
    }

    hideSpinner() {
      if (this.spinner) this.spinner.hidden = true;
    }

    showPlaybackError() {
      this.hideSpinner();
      this.message.textContent = 'This Reel could not load. Close it and try again, or choose another Reel.';
      this.message.hidden = false;
    }

    togglePlayback() {
      if (Date.now() < (this.ignoreClickUntil || 0)) return;
      this.message.hidden = true;
      if (this.video.paused) this.play();
      else this.video.pause();
    }

    updatePlayback() {
      this.playButton.toggleAttribute('data-paused', this.video.paused);
      this.playButton.setAttribute('aria-label', this.video.paused ? 'Play Reel' : 'Pause Reel');
    }

    updateSound() {
      this.soundButton.toggleAttribute('data-muted', this.video.muted);
      this.soundButton.setAttribute('aria-label', this.video.muted ? 'Unmute Reel' : 'Mute Reel');
    }

    close() {
      if (!this.viewer?.open) return;
      this.endWatch('closed');
      this.endResize();
      this.stopWatchingStickyCart();
      this.scrubbing = false;
      this.video?.pause();
      this.clipEvents?.abort();
      this.stopWatchdog();
      this.hideSpinner();
      this.stage.replaceChildren();
      this.viewer.close();
      document.body.classList.remove('cts-reels-open');
      if (this.locked) {
        document.body.style.overflow = this.previousOverflow;
        this.locked = false;
      }
      this.opener?.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.stopPreviewTrigger?.();
      this.removePreview();
      this.close();
      this.events?.abort();
      this.viewer?.remove();
      this.events = null;
      this.viewer = null;
    }
  }
  customElements.define('cts-customer-reels', CTSCustomerReels);
}
